/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_IMPERSONATION_VIEW_AS_USER
 * Date:         2026-06-23
 * Modified By:  @maya (Claude)
 *
 * Description:
 * Persistent, non-dismissible banner shown while an admin is "viewing as" a user
 * (read-only impersonation). Always visible at the top with an Exit-to-Admin button.
 *
 * File Size Check:
 * - Current: 0 lines (new file)
 * - Projected: ~55 lines
 * - Decision: Proceed (component limit 150)
 *
 * Related Screens / Modules:
 * - client/src/App.tsx
 * - client/src/services/adminUsersService.ts
 */

import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import { stopImpersonation } from "../../services/adminUsersService";

interface ImpersonationBannerProps {
  userName: string;
}

export default function ImpersonationBanner({ userName }: ImpersonationBannerProps) {
  const [exiting, setExiting] = React.useState(false);

  const handleExit = async () => {
    setExiting(true);
    try {
      await stopImpersonation();
    } catch (error) {
      // Even on error, return to admin — the cookie may already be restored
      console.error("Failed to stop impersonation:", error);
    }
    // Full reload so the restored admin cookie is picked up cleanly
    window.location.href = "/admin/users";
  };

  return (
    <div
      className="d-flex align-items-center justify-content-center gap-3 bg-warning text-dark px-3 py-2"
      style={{ position: "sticky", top: 0, zIndex: 1080 }}
      role="alert"
    >
      <span className="fw-semibold">
        <FontAwesomeIcon icon={faEye} className="me-2" />
        Acting as {userName}
      </span>
      <button
        type="button"
        className="btn btn-sm btn-dark"
        onClick={handleExit}
        disabled={exiting}
      >
        <FontAwesomeIcon icon={faRightFromBracket} className="me-2" />
        {exiting ? "Exiting…" : "Exit to Admin"}
      </button>
    </div>
  );
}
